import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsClock, BsRocketTakeoff } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useAddonTrial } from '#lib/addons/useAddonTrial';
import { BILLING_SETTINGS_PATH } from '#lib/constants/routes';
import { usePreferencesStore } from '#lib/stores/preferencesStore';

/**
 * Generic one-time informational modal shown on first entry to an add-on's area.
 * Only users who already have access reach the mount point, so it handles:
 *   - demo trial   → info + upgrade CTA
 *   - addon trial  → info ("danach <price>") + acknowledge
 * Driven by an add-on config.
 */
const AddonGateModal = ({ addon }) => {
	const navigate = useNavigate();
	const { isLoading, hasAccess, isOnTrial, trialSource, daysRemaining } = useAddonTrial(addon.key);
	const shown = usePreferencesStore((s) => Boolean(s.addonHintShown?.[addon.key]));
	const markShown = usePreferencesStore((s) => s.markAddonHintShown);

	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (isLoading || shown) return;
		if (hasAccess && isOnTrial) setOpen(true);
	}, [isLoading, shown, hasAccess, isOnTrial]);

	const close = () => {
		markShown(addon.key);
		setOpen(false);
	};

	const goToSettings = () => {
		close();
		navigate(BILLING_SETTINGS_PATH);
	};

	const isDemo = trialSource === 'demo';
	const daysText = daysRemaining === 1 ? '1 Tag' : `${daysRemaining ?? addon.trialDays} Tage`;

	return (
		<Modal isOpen={open} onClose={close} size="s">
			<Panel.Header icon={isDemo ? BsRocketTakeoff : BsClock} onClose={close}>
				Willkommen im {addon.name}
			</Panel.Header>
			<Panel.Body>
				{isDemo ? (
					<p className="text-sm text-text">
						Sie testen das {addon.name} <strong>{daysText}</strong> lang. Für dauerhaften Zugriff upgraden
						Sie auf einen kostenpflichtigen Plan.
					</p>
				) : (
					<p className="text-sm text-text">
						Ihre {addon.trialDays}-tägige {addon.name}-Testphase läuft – noch <strong>{daysText}</strong>.
						Danach {addon.priceShort}, jederzeit kündbar.
					</p>
				)}
			</Panel.Body>
			<Panel.Footer className="justify-end">
				{isDemo ? (
					<>
						<Button variant="outline" onClick={close}>
							Später
						</Button>
						<Button variant="accent" shape="pill" onClick={goToSettings}>
							Upgraden für dauerhaften Zugriff
						</Button>
					</>
				) : (
					<>
						<Button variant="outline" onClick={goToSettings}>
							Verwalten
						</Button>
						<Button onClick={close}>Verstanden</Button>
					</>
				)}
			</Panel.Footer>
		</Modal>
	);
};

AddonGateModal.propTypes = {
	addon: PropTypes.shape({
		key: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		priceShort: PropTypes.string.isRequired,
		trialDays: PropTypes.number.isRequired,
	}).isRequired,
};

export default AddonGateModal;
